import {View} from '@gongxigames/framework/slot/fragments/module/view';
import {EventDataType, IEventManagerWithAsync, ListenerType} from '@gongxigames/framework/core/types';
import {CoreEventsMap} from '@gongxigames/framework/core/events';

export abstract class ScreenOrientedView<EventsMap, SubView extends View> extends View<EventsMap> {
    public static readonly NAME: symbol = Symbol('ScreenOrientedView');

    protected abstract readonly landscapeViewName: symbol;
    protected abstract readonly portraitViewName: symbol;

    protected _views: SubView[] = [];

    private _landscapeView!: SubView;
    private _portraitView!: SubView;

    public init(): void {
        super.init();
        this.setViews(this.landscapeViewName, this.portraitViewName);
    }

    public setViews(landscapeViewName: symbol, portraitViewName: symbol): void {
        this.setLandscapeView(this._installer.getComponent<SubView>(landscapeViewName));
        this.setPortraitView(this._installer.getComponent<SubView>(portraitViewName));
    }

    public on<K extends keyof EventsMap>(id: K, callback: ListenerType<EventsMap[K]>, owner: Object, priority = 0): void {
        this.forEachViews(view => view.on(id as any, callback, priority));
    }

    public off<K extends keyof EventsMap>(id: K, owner: Object): void {
        this.forEachViews(view => view.off(id as any, owner));
    }

    public emit<K extends keyof EventsMap>(id: K, ...args: EventDataType<EventsMap[K]>): void {
        this.forEachViews(view => view.emit(id as any, ...args));
    }

    public show(): void {
        this.forEachViews(view => view.show());
    }

    public hide(): void {
        this.forEachViews(view => view.hide());
    }

    public isVisible(): boolean {
        let isVisible = false;

        this.forEachViews(view => {
            if (!isVisible && view.isVisible()) {
                isVisible = true;
            }
        });

        return isVisible;
    }

    public onLandscape(): void {
        this._portraitView.removeFromParent();
        this._landscapeView.addToParent();
    }

    public onPortrait(): void {
        this._landscapeView.removeFromParent();
        this._portraitView.addToParent();
    }

    public getViewDependencies(): (symbol|string)[] {
        return [this.landscapeViewName, this.portraitViewName];
    }

    public forEachViews(callback: (view: SubView) => void): void {
        this._views.forEach(callback);
    }

    protected setupEvents(): void {
        const eventManager = (this.eventManager as IEventManagerWithAsync<CoreEventsMap>);

        eventManager.on('General.Screen.LANDSCAPE', this.onLandscape.bind(this), this);
        eventManager.on('General.Screen.PORTRAIT', this.onPortrait.bind(this), this);
        eventManager.on('General.Screen.RESIZED', this.onResized.bind(this), this);
    }

    protected removeEvents(): void {
        const eventManager = (this.eventManager as IEventManagerWithAsync<CoreEventsMap>);

        eventManager.off('General.Screen.LANDSCAPE', this);
        eventManager.off('General.Screen.PORTRAIT', this);
        eventManager.off('General.Screen.RESIZED', this);
    }

    private setLandscapeView(value: SubView): void {
        this._landscapeView = value;
        this.addView(this._landscapeView);
    }

    private setPortraitView(value: SubView): void {
        this._portraitView = value;
        this.addView(this._portraitView);
    }

    private addView(view: SubView): void {
        view.removeFromParent();
        view.parent = this.container;
        this._views.push(view);
    }
}
